import { useTranslation } from 'react-i18next';
import { isPartialPeriod, resolvePeriodFreshness, type PeriodFreshnessKind } from '@/application/metrics/periodFreshness';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';

interface PeriodFreshnessHintProps {
  /** `shell`: junto al chip (oculto en mobile). `page`: cabecera de dominio. */
  placement?: 'shell' | 'page';
}

function copyKey(kind: PeriodFreshnessKind): string {
  switch (kind) {
    case 'current-month':
      return 'period.freshness.currentMonth';
    case 'current-year':
      return 'period.freshness.currentYear';
    case 'closed-month':
      return 'period.freshness.closedMonth';
    case 'closed-year':
      return 'period.freshness.closedYear';
  }
}

/**
 * Hint de si el periodo es parcial (mes/año en curso) o ya cerró.
 */
export function PeriodFreshnessHint({ placement = 'shell' }: PeriodFreshnessHintProps) {
  const { t } = useTranslation();
  const { period, year, month, granularity } = useGlobalPeriod();
  const kind = resolvePeriodFreshness({ period, year, month, granularity });
  const partial = isPartialPeriod(kind);
  const label = t(copyKey(kind));
  const title = partial ? t('period.freshness.untilToday') : undefined;

  const placementClass =
    placement === 'shell'
      ? 'hidden max-w-[11rem] truncate md:inline-flex'
      : 'inline-flex max-w-full';

  return (
    <span
      data-testid="period-freshness"
      data-partial={partial ? 'true' : 'false'}
      title={title}
      className={`${placementClass} items-center text-[10px] font-medium leading-tight text-[var(--color-text-subtle)] sm:text-[11px]`}
    >
      {label}
    </span>
  );
}
